"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DialogWidget = exports.DialogBorderWidth = exports.SharedWidgetsDialogLabel = exports.DialogUserResizeMode = exports.DialogType = exports.DialogPositioningBehavior = void 0;
var tslib_1 = require("tslib");
var throttle = require("lodash/throttle");
var React = require("react");
var ReactDOM = require("react-dom");
var PreventUserSelect_1 = require("../../styles/PreventUserSelect");
var TabStyles_1 = require("../../styles/TabStyles");
var Declarative_1 = require("../../utils/Declarative");
var Geometry_1 = require("../../utils/Geometry");
var GlobalPointerEventCapturer_1 = require("../../utils/GlobalPointerEventCapturer");
var Math_1 = require("../../utils/Math");
var OrientationChange_1 = require("../../utils/OrientationChange");
var Overlay_1 = require("../../utils/Overlay");
var ViewportConstrainedDom_1 = require("../../utils/ViewportConstrainedDom");
var ViewportHelpers = require("../../utils/ViewportHelpers");
var FloaterWidget_1 = require("../FloaterWidget");
var GlassWidget_1 = require("../GlassWidget");
var DialogContent_1 = require("./DialogContent");
var DialogPositioningBehavior;
(function (DialogPositioningBehavior) {
    /** Centers the dialog on the page */
    DialogPositioningBehavior[DialogPositioningBehavior["Centered"] = 0] = "Centered";
    /** Maximizes the dialog */
    DialogPositioningBehavior[DialogPositioningBehavior["Maximized"] = 1] = "Maximized";
    /** Makes an initally centered dialog that can be dragged (Will not work with DialogType.Uber) */
    DialogPositioningBehavior[DialogPositioningBehavior["Draggable"] = 2] = "Draggable";
    /** Positions the dialog relative to an anchor element */
    DialogPositioningBehavior[DialogPositioningBehavior["Anchored"] = 3] = "Anchored";
})(DialogPositioningBehavior = exports.DialogPositioningBehavior || (exports.DialogPositioningBehavior = {}));
var DialogType;
(function (DialogType) {
    /** Dialog with a title bar. It can be draggable. */
    DialogType[DialogType["NormalWithTitleBar"] = 0] = "NormalWithTitleBar";
    /** Dialog without a title bar, but has a header space that makes it draggable */
    DialogType[DialogType["NormalWithoutTitleBar"] = 1] = "NormalWithoutTitleBar";
    /** Dialog with small top padding. It is not draggable. */
    DialogType[DialogType["Uber"] = 2] = "Uber";
    /** Dialog with a large title text but no visible bar. It can be draggable. */
    DialogType[DialogType["LightweightTitleBar"] = 3] = "LightweightTitleBar";
})(DialogType = exports.DialogType || (exports.DialogType = {}));
/**
 * Different modes for user-based dialog resizing (aka via drag handle).
 */
var DialogUserResizeMode;
(function (DialogUserResizeMode) {
    /** The dialog cannot be resized */
    DialogUserResizeMode["NotResizable"] = "not-resizable";
    /** The dialog can be resized horizontally */
    DialogUserResizeMode["Horizontal"] = "horizontal";
    /** The dialog can be resized vertically */
    DialogUserResizeMode["Vertical"] = "vertical";
    /** The dialog can be resized both vertically and horizontally */
    DialogUserResizeMode["Both"] = "both";
})(DialogUserResizeMode = exports.DialogUserResizeMode || (exports.DialogUserResizeMode = {}));
// These are exported for test purposes only
exports.SharedWidgetsDialogLabel = 'SharedWidgetsDialogLabel';
exports.DialogBorderWidth = TabStyles_1.TabStyles.Sizing.BorderWidth;
var ResizeAndRepositionDelayMs = 500;
var ResizeThrottleTime = 100;
var ViewportHorizontalMargins = 40;
var ViewportVerticalMargins = 40;
var dialogInstanceCount = 0;
var getStyles = function () {
    return {
        Dialog: {
            backgroundColor: TabStyles_1.TabStyles.Colors.White,
            borderColor: TabStyles_1.TabStyles.Colors.L5,
            borderRadius: TabStyles_1.TabStyles.Sizing.BorderRadius,
            borderStyle: 'solid',
            borderWidth: exports.DialogBorderWidth,
            boxShadow: TabStyles_1.TabStyles.Shadow.Elevated,
            cursor: 'default',
            display: 'flex',
            flexDirection: 'column',
        }
    };
};
// tslint:disable-next-line:class-name
var DialogWidget = /** @class */ (function (_super) {
    tslib_1.__extends(DialogWidget, _super);
    function DialogWidget(props) {
        var _this = _super.call(this, props) || this;
        _this.dialogResizingEventCapturer = null;
        _this.throttledHandleAdjustPositionOnResize = throttle(function () { return _this.handleAdjustPositionOnResize(); }, ResizeThrottleTime);
        /**
         * On a resize event and if shouldAdjustPositionOnResizeAndIsNonMobile is true, this will fired
         * and try to fit the dialog on the screen depending upon the positioning behavior and state of
         * the dialog.
         */
        _this.handleAdjustPositionOnResize = function () {
            if (!_this.props.shouldAdjustPositionOnWindowResize) {
                return;
            }
            /**
             * Defect 646231:Dialog (Beta) Issue: Inconsistent resizing
             * On window resizes we were not resizing the sizingWrapper element. As a result, the content
             * within the Dialog Widget would resize; however the sizingWrapper element would not.
             * The end user effect was inconsistent resizing of the Dialog Widget and visual artifacts.
             */
            if (_this.sizingWrapper) {
                _this.sizingWrapper.updateSize();
            }
            _this.repositionDialog();
        };
        _this.startResizing = function (e) {
            if (e.target === e.currentTarget) {
                e.preventDefault();
                var startingRect_1 = _this.floaterWidget.getFloaterClientRect();
                var cardinal_1 = e.currentTarget.getAttribute('data-cardinal');
                _this.dialogResizingEventCapturer = new GlobalPointerEventCapturer_1.GlobalPointerEventCapturer(ReactDOM.findDOMNode(_this.sizingWrapper), function (mouseMoveEvent) { return _this.resizeDialog(mouseMoveEvent, startingRect_1, cardinal_1); }, _this.stopResizing);
                _this.dialogResizingEventCapturer.addListeners();
                _this.setState({ isResizing: true });
            }
        };
        _this.stopResizing = function () {
            var _a;
            (_a = _this.dialogResizingEventCapturer) === null || _a === void 0 ? void 0 : _a.removeListeners();
            _this.dialogResizingEventCapturer = null;
            _this.setState({ isResizing: false });
            if (_this.props.onUserResizeEnd) {
                _this.props.onUserResizeEnd(_this.state.width, _this.state.height);
            }
        };
        _this.handleFloaterWidgetRef = function (ref) {
            _this.floaterWidget = ref;
        };
        _this.handleOverlayRef = function (ref) {
            _this.overlay = ref;
        };
        _this.handleSizingWrapperRef = function (ref) {
            _this.sizingWrapper = ref;
        };
        _this.getDialogFloaterClientRect = function () {
            if (_this.floaterWidget) {
                return _this.floaterWidget.getFloaterClientRect();
            }
            throw new Error("Can't get draggable size of non-draggable dialog");
        };
        _this.dragBoundedDialogFloaterPosition = function (pos) {
            if (!_this.state.hasBeenDragged) {
                _this.setState({ hasBeenDragged: true });
            }
            if (_this.floaterWidget) {
                _this.floaterWidget.setPosition(_this.getBoundedFloaterPos(pos));
            }
        };
        _this.handleOrientationChange = function () {
            if (_this.resizeAndRepositionTimeoutId) {
                /*
                 * A new orientation change has occurred while waiting for the resize/reposition delay. We don't actually need
                 * to do the preparation routine, we just need to reset the delay.
                 */
                clearTimeout(_this.resizeAndRepositionTimeoutId);
                _this.resizeAndRepositionTimeoutId = window.setTimeout(_this.resizeAndRepositionCallback, ResizeAndRepositionDelayMs);
                return;
            }
            /**
             * After an orientation change the dialog may be in a position where it skews the visual viewport by being off the
             * page, so we need to hide it first.
             */
            _this.hideDialogAndThen(function (redisplayCallback) {
                /**
                 * For whatever reason iOS Safari is slow to update the visual viewport after hiding the dialog after an orientation
                 * change. The hack below fixes the visual jerkiness, but we still need a setTimeout for the internal values to update.
                 */
                _this.forceRepaintHack();
                /* Delay the resize and reposition so that we get the correct viewport values. */
                _this.resizeAndRepositionCallback = function () {
                    _this.resizeAndRepositionTimeoutId = null;
                    _this.resizeAndRepositionCallback = null;
                    _this.sizingWrapper.updateSize();
                    if (redisplayCallback) {
                        redisplayCallback();
                    }
                    _this.repositionDialog();
                };
                _this.resizeAndRepositionTimeoutId = window.setTimeout(_this.resizeAndRepositionCallback, ResizeAndRepositionDelayMs);
            });
        };
        // tslint:disable:deprecation
        _this.getAnchorClientRect = function () {
            if (_this.props.getAnchorClientRect) {
                return _this.props.getAnchorClientRect();
            }
            else if (_this.props.anchorEl) {
                return _this.props.anchorEl.getBoundingClientRect();
            }
            else {
                return undefined;
            }
        };
        _this.handleGlassEscape = function () {
            _this.props.onCloseRequest();
        };
        _this.instanceId = dialogInstanceCount++;
        _this.state = { hasBeenDragged: false, height: props.dialogHeight, isResizing: false, width: props.dialogWidth };
        return _this;
    }
    DialogWidget.prototype.addResizeEventListener = function () {
        window.addEventListener('resize', this.throttledHandleAdjustPositionOnResize);
    };
    DialogWidget.prototype.removeResizeEventListener = function () {
        window.removeEventListener('resize', this.throttledHandleAdjustPositionOnResize);
    };
    DialogWidget.prototype.resizeDialog = function (e, rect, cardinal) {
        e.preventDefault();
        var _a = this.props, dialogMinHeight = _a.dialogMinHeight, dialogMinWidth = _a.dialogMinWidth;
        var position = { top: rect.top, left: rect.left };
        var dimensions = { height: rect.height - (exports.DialogBorderWidth * 2), width: rect.width - (exports.DialogBorderWidth * 2) };
        var clientY = Math_1.clamp(e.clientY, 0, document.documentElement.clientHeight);
        var clientX = Math_1.clamp(e.clientX, 0, document.documentElement.clientWidth);
        if (cardinal.indexOf('s') >= 0) {
            dimensions.height = Math.max(clientY - rect.top, dialogMinHeight) - (exports.DialogBorderWidth * 2);
        }
        else if (cardinal.indexOf('n') >= 0) {
            dimensions.height = rect.bottom - clientY - (exports.DialogBorderWidth * 2);
            if (dimensions.height >= dialogMinHeight) {
                position.top = clientY;
            }
            else {
                dimensions.height = dialogMinHeight - (exports.DialogBorderWidth * 2);
                position.top = rect.bottom - dimensions.height - (exports.DialogBorderWidth * 2);
            }
        }
        if (cardinal.indexOf('e') >= 0) {
            dimensions.width = Math.max(clientX - rect.left, dialogMinWidth) - (exports.DialogBorderWidth * 2);
        }
        else if (cardinal.indexOf('w') >= 0) {
            dimensions.width = rect.right - clientX - (exports.DialogBorderWidth * 2);
            if (dimensions.width >= dialogMinWidth) {
                position.left = clientX;
            }
            else {
                dimensions.width = dialogMinWidth - (exports.DialogBorderWidth * 2);
                position.left = rect.right - dimensions.width - (exports.DialogBorderWidth * 2);
            }
        }
        if (this.floaterWidget) {
            this.floaterWidget.setPosition(position);
            this.setState(dimensions);
        }
    };
    DialogWidget.prototype.repositionDialog = function () {
        switch (this.props.positioningBehavior) {
            case DialogPositioningBehavior.Centered:
            case DialogPositioningBehavior.Maximized:
                this.floaterWidget.centerInVisualViewport();
                break;
            case DialogPositioningBehavior.Draggable:
                if (this.state.hasBeenDragged) {
                    this.repositionDraggedDialog();
                }
                else {
                    this.floaterWidget.centerInVisualViewport();
                }
                break;
            case DialogPositioningBehavior.Anchored:
                if (this.overlay) {
                    this.overlay.setPlacement();
                }
                break;
            default:
                throw new Error("Invalid Dialog Positioning Behavior: " + this.props.positioningBehavior);
        }
    };
    /**
     * We save the dragged dialog position before hiding it because it is needed to reposition the dragged dialog.
     */
    DialogWidget.prototype.saveDraggedDialogPositionBeforeHide = function () {
        this.dragPositionBeforeHide = this.getDialogFloaterClientRect();
    };
    /**
     * To reposition a dragged dialog we need to take into account if it was hidden.  If it was hidden we use the
     * position before it was hidden. We need to use a single setPosition instead of one each for unhiding and repositioning
     * the dialog because if the original position before it was hidden is to the right of the viewport mobile chrome will try
     * to zoom out to show the dialog.
     */
    DialogWidget.prototype.repositionDraggedDialog = function () {
        var draggedOriginalPosition;
        if (!!this.dragPositionBeforeHide) {
            draggedOriginalPosition = this.dragPositionBeforeHide;
            // set it as undefined so if a reposition occurs from an window resize change it will not set this hide variable
            this.dragPositionBeforeHide = undefined;
        }
        else {
            draggedOriginalPosition = this.getDialogFloaterClientRect();
        }
        this.floaterWidget.setPosition(this.getBoundedFloaterPos(draggedOriginalPosition));
    };
    /**
     * Hides the dialog and then invokes the given callback. The given callback will be invoked with a reposition callback
     * as an argument. When invoked, the reposition callback will unhide and reposition the dialog.
     */
    DialogWidget.prototype.hideDialogAndThen = function (afterHideCallback) {
        var redisplayCallback = function () { return; };
        switch (this.props.positioningBehavior) {
            case DialogPositioningBehavior.Centered:
            case DialogPositioningBehavior.Maximized:
                this.floaterWidget.setPosition({ top: -10000, left: -10000 });
                break;
            case DialogPositioningBehavior.Draggable:
                this.saveDraggedDialogPositionBeforeHide();
                this.floaterWidget.setPosition({ top: -10000, left: -10000 });
                break;
            case DialogPositioningBehavior.Anchored:
                var node_1 = ReactDOM.findDOMNode(this.sizingWrapper);
                var savedDisplay_1 = node_1.style.display;
                node_1.style.display = 'none';
                redisplayCallback = function () {
                    node_1.style.display = savedDisplay_1;
                };
                break;
            default:
                throw new Error("Invalid Dialog Positioning Behavior: " + this.props.positioningBehavior);
        }
        afterHideCallback(redisplayCallback);
    };
    /* Forces a repaint in WebKit by querying offsetHeight. */
    DialogWidget.prototype.forceRepaintHack = function () {
        /**
         * The code here is adapted from
         * http://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
         */
        var node = ReactDOM.findDOMNode(this);
        var savedDisplay = node.style.display;
        node.style.display = 'none';
        // tslint:disable-next-line:no-unused-expression
        node.offsetHeight;
        node.style.display = savedDisplay;
    };
    /**
     * Adjusts the given position if needed to keep the dialog on screen.
     */
    DialogWidget.prototype.getBoundedFloaterPos = function (pos) {
        var dialogClientRect = this.floaterWidget.getFloaterClientRect();
        var dialogRectAtGivenPos = {
            top: pos.top,
            left: pos.left,
            width: dialogClientRect.width,
            height: dialogClientRect.height
        };
        var visualViewport = ViewportHelpers.getVisualViewport();
        var windowRect = {
            top: 0,
            left: 0,
            width: visualViewport.width,
            height: visualViewport.height
        };
        return Geometry_1.computePositionToContainBounds(dialogRectAtGivenPos, windowRect);
    };
    // tslint:enable:deprecation
    DialogWidget.prototype.getPositionedDialogElem = function (dialogBody) {
        switch (this.props.positioningBehavior) {
            case DialogPositioningBehavior.Centered:
            case DialogPositioningBehavior.Draggable:
            case DialogPositioningBehavior.Maximized:
                var id = Declarative_1.makeTestId(this.props.testId, 'Dialog');
                return React.createElement(FloaterWidget_1.FloaterWidget, { portalEventTrapOverrides: this.props.portalEventTrapOverrides, testId: id, 
                    // tslint:disable-next-line:deprecation
                    stackingRootId: this.props.stackingRootId, ref: this.handleFloaterWidgetRef, zIndex: this.props.zIndex, shouldRecenterOnUpdate: !!this.props.shouldRecenterOnUpdate && !this.state.hasBeenDragged, wrapperTestId: Declarative_1.makeTestId(id, 'Floater'), children: dialogBody });
            case DialogPositioningBehavior.Anchored:
                var overlayProps = {
                    portalEventTrapOverrides: this.props.portalEventTrapOverrides,
                    getAnchorClientRect: this.getAnchorClientRect,
                    anchorOrigin: this.props.anchorOrigin || {
                        vertical: Overlay_1.VerticalOriginOption.bottom,
                        horizontal: Overlay_1.HorizontalOriginOption.left
                    },
                    stackedOrigin: this.props.stackedOrigin || {
                        vertical: Overlay_1.VerticalOriginOption.top,
                        horizontal: Overlay_1.HorizontalOriginOption.left
                    },
                    // tslint:disable-next-line:deprecation
                    stackingRootId: this.props.stackingRootId,
                    allowAutoPosition: true,
                    zIndex: this.props.zIndex,
                    leftFromOverlaidPosition: this.props.dialogStyle ? parseFloat(this.props.dialogStyle.left) || 0 : undefined,
                    topFromOverlaidPosition: this.props.dialogStyle ? parseFloat(this.props.dialogStyle.top) || 0 : undefined,
                };
                return React.createElement(Overlay_1.Overlay, tslib_1.__assign({}, overlayProps, { ref: this.handleOverlayRef, children: dialogBody }));
            default:
                throw new Error("Invalid Dialog Positioning Behavior: " + this.props.positioningBehavior);
        }
    };
    DialogWidget.prototype.getGlassElem = function () {
        var id = Declarative_1.makeTestId(this.props.testId, 'Dialog');
        return (React.createElement(GlassWidget_1.GlassWidget, { testId: id, handleClick: this.props.handleGlassClick, isTransparent: this.props.isGlassTransparent, 
            // tslint:disable-next-line:deprecation
            stackingRootId: this.props.stackingRootId, zIndex: this.props.zIndex, handleEscape: this.handleGlassEscape, wrapperTestId: Declarative_1.makeTestId(id, 'Glass') }));
    };
    DialogWidget.prototype.componentDidMount = function () {
        this.warnIfNoTitleProp();
        if (this.props.initialPosition && this.props.positioningBehavior === DialogPositioningBehavior.Draggable) {
            this.floaterWidget.setPosition(this.getBoundedFloaterPos(this.props.initialPosition));
        }
        OrientationChange_1.OrientationChange.addListener(this.handleOrientationChange);
        if (this.props.shouldAdjustPositionOnWindowResize) {
            this.addResizeEventListener();
        }
    };
    DialogWidget.prototype.componentDidUpdate = function (prevProps) {
        this.warnIfNoTitleProp();
        if (this.props.shouldAdjustPositionOnWindowResize !== prevProps.shouldAdjustPositionOnWindowResize) {
            this.removeResizeEventListener();
            if (this.props.shouldAdjustPositionOnWindowResize) {
                this.addResizeEventListener();
            }
        }
        this.resizeDialogIfResizeModeChanged(prevProps.userResizeMode, this.props.userResizeMode);
    };
    /** It is a historical mistake that this prop is not marked as required; to avoid build breakages we console warn instead. */
    DialogWidget.prototype.warnIfNoTitleProp = function () {
        if (this.props.title === undefined) {
            // tslint:disable-next-line:no-console
            console.trace('`title` prop must be provided for all dialog types per accessibility requirements');
        }
    };
    /** if dimension(s) changed from not-resizable to resizable or vice-versa, resets the dimension(s) state with the dimension prop(s) */
    DialogWidget.prototype.resizeDialogIfResizeModeChanged = function (prevResizeMode, newResizeMode) {
        var wasHorizontallyResizable = prevResizeMode === 'horizontal' || prevResizeMode === 'both';
        var isHorizontallyResizable = newResizeMode === 'horizontal' || newResizeMode === 'both';
        if (wasHorizontallyResizable !== isHorizontallyResizable) {
            this.setState({ width: this.props.dialogWidth });
        }
        var wasVerticallyResizable = prevResizeMode === 'vertical' || prevResizeMode === 'both';
        var isVerticallyResizable = newResizeMode === 'vertical' || newResizeMode === 'both';
        if (wasVerticallyResizable !== isVerticallyResizable) {
            this.setState({ height: this.props.dialogHeight });
        }
    };
    DialogWidget.prototype.componentWillUnmount = function () {
        OrientationChange_1.OrientationChange.removeListener(this.handleOrientationChange);
        this.removeResizeEventListener();
        if (this.dialogResizingEventCapturer) {
            this.dialogResizingEventCapturer.removeListeners();
        }
    };
    DialogWidget.prototype.render = function () {
        var dialogEl = React.createElement(ViewportConstrainedDom_1.ViewportConstrainedDomWrapper, { isMaximized: this.props.positioningBehavior === DialogPositioningBehavior.Maximized, horizontalMargins: ViewportHorizontalMargins, verticalMargins: ViewportVerticalMargins, style: tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, getStyles().Dialog), this.props.dialogStyle), { width: this.state.width, height: this.state.height }), ref: this.handleSizingWrapperRef, testId: Declarative_1.conditional(this.props.positioningBehavior === DialogPositioningBehavior.Anchored, Declarative_1.makeTestId(this.props.testId, 'Dialog')) },
            React.createElement(DialogContent_1.DialogContent, tslib_1.__assign({}, this.props, { dialogContentStyle: tslib_1.__assign(tslib_1.__assign({}, this.props.dialogContentStyle), (this.state.isResizing && { pointerEvents: 'none' })), id: "" + exports.SharedWidgetsDialogLabel + this.instanceId, getDraggableDialogClientRect: this.getDialogFloaterClientRect, setDraggableDialogPosition: this.dragBoundedDialogFloaterPosition, testId: Declarative_1.makeTestId(this.props.testId, 'Dialog') })),
            this.props.userResizeMode !== DialogUserResizeMode.NotResizable && this.renderResizeHandles());
        return (React.createElement("div", { style: { display: 'none' } },
            Declarative_1.conditional(this.props.isModal, this.getGlassElem()),
            this.getPositionedDialogElem(dialogEl)));
    };
    DialogWidget.prototype.renderResizeHandles = function () {
        var userResizeMode = this.props.userResizeMode;
        return React.createElement(React.Fragment, null,
            (userResizeMode === DialogUserResizeMode.Vertical || userResizeMode === DialogUserResizeMode.Both) && React.createElement(React.Fragment, null,
                React.createElement(VerticalResizeHandle, { "data-cardinal": 'n', onMouseDown: this.startResizing, style: { top: -3 } }),
                React.createElement(VerticalResizeHandle, { "data-cardinal": 's', onMouseDown: this.startResizing, style: { bottom: -3 } })),
            (userResizeMode === DialogUserResizeMode.Horizontal || userResizeMode === DialogUserResizeMode.Both) && React.createElement(React.Fragment, null,
                React.createElement(HorizontalResizeHandle, { "data-cardinal": 'w', onMouseDown: this.startResizing, style: { left: -3 } }),
                React.createElement(HorizontalResizeHandle, { "data-cardinal": 'e', onMouseDown: this.startResizing, style: { right: -3 } })),
            userResizeMode === DialogUserResizeMode.Both && React.createElement(React.Fragment, null,
                React.createElement(CornerResizeHandle, { "data-cardinal": 'nw', onMouseDown: this.startResizing, style: { top: -3, left: -3, cursor: 'nwse-resize' } }),
                React.createElement(CornerResizeHandle, { "data-cardinal": 'se', onMouseDown: this.startResizing, style: { bottom: -3, right: -3, cursor: 'nwse-resize' } }),
                React.createElement(CornerResizeHandle, { "data-cardinal": 'ne', onMouseDown: this.startResizing, style: { top: -3, right: -3, cursor: 'nesw-resize' } }),
                React.createElement(CornerResizeHandle, { "data-cardinal": 'sw', onMouseDown: this.startResizing, style: { bottom: -3, left: -3, cursor: 'nesw-resize' } })));
    };
    DialogWidget.defaultProps = {
        dialogMinHeight: 100,
        dialogMinWidth: 100,
        userResizeMode: DialogUserResizeMode.NotResizable,
    };
    return DialogWidget;
}(React.Component));
exports.DialogWidget = DialogWidget;
var cancelEvent = function (e) { return e.preventDefault(); };
var ignoreTouchEvents = function (ref) { return ref === null || ref === void 0 ? void 0 : ref.addEventListener('touchstart', cancelEvent, { passive: false }); };
var ResizeHandle = function (props) {
    return React.createElement("div", tslib_1.__assign({}, props, { ref: ignoreTouchEvents, style: tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, PreventUserSelect_1.PreventUserSelect), { position: 'absolute' }), props.style) }));
};
ResizeHandle.displayName = 'ResizeHandle';
var VerticalResizeHandle = function (props) {
    return React.createElement(ResizeHandle, tslib_1.__assign({}, props, { style: tslib_1.__assign({ cursor: 'ns-resize', left: 0, right: 0, height: 8 }, props.style) }));
};
VerticalResizeHandle.displayName = 'VerticalResizeHandle';
var HorizontalResizeHandle = function (props) {
    return React.createElement(ResizeHandle, tslib_1.__assign({}, props, { style: tslib_1.__assign({ cursor: 'ew-resize', top: 0, bottom: 0, width: 8 }, props.style) }));
};
HorizontalResizeHandle.displayName = 'HorizontalResizeHandle';
var CornerResizeHandle = function (props) {
    return React.createElement(ResizeHandle, tslib_1.__assign({}, props, { style: tslib_1.__assign({ height: 8, width: 8 }, props.style) }));
};
CornerResizeHandle.displayName = 'CornerResizeHandle';
//# sourceMappingURL=DialogWidget.js.map